if (!customElements.get("order-countdown")) {
  class OrderCountdown extends HTMLElement {
    static MIN_DAYS_FOR_SPECIFIC_DATE = 2;

    constructor() {
      super();
      this.nonWorkingDates = [];
      this.cutoffHour = 15;
      this.countdownInterval = null;
      this.languageIsoCode = this.dataset.isoCodeLanguage || "bg";
      this.countryIsoCode = this.dataset.isoCodeCountry || "BG";
      this.timeZone = this.dataset.timezone || "Europe/Sofia";
      this.textContainer = this.querySelector("[js-text]");
      this.templateHTML = this.textContainer?.getAttribute("js-text") || "";
    
      this._dateFormatter = null;
      this._monthFormatter = null;

      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    get shopLocale() {
      return `${this.languageIsoCode}-${this.countryIsoCode}`;
    }

    get shopDate() {
      if (!this._dateFormatter) {
        this._dateFormatter = new Intl.DateTimeFormat(this.shopLocale, {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      }

      const parts = this._dateFormatter.formatToParts(new Date());
      const map = Object.fromEntries(
        parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value])
      );

      return new Date(`${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`);
    }

    handleVariantChange(event) {
      const available = event.detail?.variant?.available;

      this.classList.toggle('hidden', !available);
    }

    connectedCallback() {
      this.initializeSettings();
      this.startCountdown();

      document.addEventListener('variant-change', this.handleVariantChange);
    }

    disconnectedCallback() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
    }

    initializeSettings() {
      const nonWorkingDatesStr = this.dataset.nonWorkingDates || "";
      this.cutoffHour = parseInt(this.dataset.cutoffHour) || 15;
      this.nonWorkingDates = nonWorkingDatesStr
        ? nonWorkingDatesStr
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean)
        : [];
    }

    startCountdown() {
      this.updateMessage();
      this.countdownInterval = setInterval(() => this.updateMessage(), 1000);
    }

    updateMessage() {
      if (!this.textContainer || !this.templateHTML.includes("[time]")) return;

      const now = this.shopDate;
      const remainingTime = this.getRemainingTime(now);
      const targetDate = this.getTargetDate(now);
      const days = Math.ceil(remainingTime / (1000 * 60 * 60 * 24));
      const strings = window.themeStrings || {};
      let timeHTML, whenHTML;

      if (days >= OrderCountdown.MIN_DAYS_FOR_SPECIFIC_DATE) {
        if (!this._monthFormatter) {
          this._monthFormatter = new Intl.DateTimeFormat(this.shopLocale, { month: 'long' });
        }
        
        timeHTML = `<span>${days}</span> ${strings.days || "дни"}`;

        const day = targetDate.getDate();
        const month = this._monthFormatter.format(targetDate);

        whenHTML = (strings.on_date || "на [date] [month]")
          .replace("[date]", day)
          .replace("[month]", month);
      } else {
        const { hours, minutes, seconds } = this.splitTime(remainingTime);
        timeHTML = `<span>${hours}</span> : <span>${minutes}</span> : <span>${seconds}</span> ${strings.hours || "часа"}`;
        whenHTML = this.isToday(targetDate, now)
          ? strings.today || "днес"
          : strings.tomorrow || "утре";
      }

      let result = this.templateHTML.replace("[time]", timeHTML);
      result = result.replace("[when]", whenHTML);

      this.textContainer.innerHTML = result;
    }

    isToday(targetDate, now) {
      return this.formatDate(targetDate) === this.formatDate(now);
    }

    getTargetDate(currentDate) {
      let targetDate = new Date(currentDate);

      if (currentDate.getHours() >= this.cutoffHour) {
        targetDate.setDate(targetDate.getDate() + 1);
      }

      targetDate = this.getNextWorkingDay(targetDate);
      targetDate.setHours(this.cutoffHour, 0, 0, 0);

      return targetDate;
    }

    getRemainingTime(currentDate) {
      const targetDate = this.getTargetDate(currentDate);
      return Math.max(0, targetDate.getTime() - currentDate.getTime());
    }

    getNextWorkingDay(date) {
      let next = new Date(date);
      while (this.isNonWorkingDay(next)) {
        next.setDate(next.getDate() + 1);
      }
      return next;
    }

    isNonWorkingDay(date) {
      const day = date.getDay();
      if (day === 0 || day === 6) return true;
      return this.nonWorkingDates.includes(this.formatDate(date));
    }

    formatDate(date) {
      const d = date.getDate().toString().padStart(2, "0");
      const m = (date.getMonth() + 1).toString().padStart(2, "0");
      return `${date.getFullYear()}-${m}-${d}`;
    }

    splitTime(ms) {
      const total = Math.floor(ms / 1000);
      return {
        hours: Math.floor(total / 3600)
          .toString()
          .padStart(2, "0"),
        minutes: Math.floor((total % 3600) / 60)
          .toString()
          .padStart(2, "0"),
        seconds: (total % 60).toString().padStart(2, "0"),
      };
    }
  }

  customElements.define("order-countdown", OrderCountdown);
}
